import { Controller } from "@hotwired/stimulus"
import { Calendar } from '@fullcalendar/core'
import timeGridPlugin from '@fullcalendar/timegrid'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'

export default class extends Controller {
  static values = {
    teamName: String,
    schedules: Array,
    users: Array,
    teams: Array,
    canEdit: Boolean
  }

  static targets = ["calendar", "teamSelect", "userSelect", "modal", "form", "modalTitle", "scheduleId",
                    "userIdSelect", "daySelect", "startTimeInput", "endTimeInput", "errors"]

  calendar = null

  connect() {
    this.calendar = new Calendar(this.calendarTarget, {
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
      initialView: 'timeGridWeek',
      headerToolbar: {
        left: 'prev,next today',
        center: '',
        right: 'timeGridWeek,timeGridDay'
      },
      dayHeaderFormat: { weekday: 'short' },
      titleFormat: { year: 'numeric', month: 'short', day: 'numeric' },
      eventTimeFormat: { hour: '2-digit', minute: '2-digit', hour12: false },
      height: 'auto',
      slotDuration: '01:00:00',
      slotMinTime: '06:00:00',
      slotMaxTime: '20:00:00',
      allDaySlot: false,
      editable: this.canEditValue,
      selectable: this.canEditValue,
      events: this.schedulesValue,
      eventDrop: this.handleDrop.bind(this),
      eventResize: this.handleResize.bind(this),
      select: this.handleSelect.bind(this),
      eventClick: this.handleClick.bind(this),
      eventDidMount: (info) => {
        if (window.bootstrap && window.bootstrap.Tooltip) {
          new bootstrap.Tooltip(info.el, {
            title: `${info.event.title} - ${info.event.start.toLocaleTimeString('pt-BR', {hour: '2-digit', minute:'2-digit'})} às ${info.event.end.toLocaleTimeString('pt-BR', {hour: '2-digit', minute:'2-digit'})}`
          })
        }
      }
    })

    this.calendar.render()

    // Setup form submission if modal exists
    if (this.hasFormTarget) {
      console.log('Adding submit listener to form')
      this.formTarget.addEventListener('submit', (e) => this.handleSubmit(e))
    }

    // Carregar equipes no select
    if (this.hasTeamSelectTarget) {
      this.populateTeams()
    }
  }

  populateTeams() {
    this.teamSelectTarget.innerHTML = '<option value="">Todos os times</option>'
    this.teamsValue.forEach(team => {
      const option = document.createElement('option')
      option.value = team
      option.textContent = team
      if (team === this.teamNameValue) option.selected = true
      this.teamSelectTarget.appendChild(option)
    })
  }

  filterTeam(event) {
    const team = event.target.value
    const params = new URLSearchParams()
    if (team) params.set('team', team)
    if (this.userSelectTarget?.value) params.set('user_id', this.userSelectTarget.value)
    window.location.href = `/grade-horaria${params.toString() ? '?' + params.toString() : ''}`
  }

  filterUser(event) {
    const userId = event.target.value
    const params = new URLSearchParams()
    if (this.teamSelectTarget?.value) params.set('team', this.teamSelectTarget.value)
    if (userId) params.set('user_id', userId)
    window.location.href = `/grade-horaria${params.toString() ? '?' + params.toString() : ''}`
  }

  async handleDrop(info) {
    const event = info.event
    const start = this.formatTime(event.start)
    const end = this.formatTime(event.end)
    const day = event.start.getDay()

    try {
      const response = await fetch(`/team_schedules/${event.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken()
        },
        body: JSON.stringify({
          team_schedule: {
            day_of_week: day,
            start_time: start,
            end_time: end
          }
        })
      })

      if (!response.ok) {
        const result = await response.json()
        info.revert()
        alert(result.errors?.join(', ') || 'Erro ao atualizar')
      }
    } catch (error) {
      info.revert()
      alert('Erro ao atualizar horário')
    }
  }

  async handleResize(info) {
    const event = info.event
    const start = this.formatTime(event.start)
    const end = this.formatTime(event.end)
    const day = event.start.getDay()

    try {
      const response = await fetch(`/team_schedules/${event.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken()
        },
        body: JSON.stringify({
          team_schedule: {
            day_of_week: day,
            start_time: start,
            end_time: end
          }
        })
      })

      if (!response.ok) {
        const result = await response.json()
        info.revert()
        alert(result.errors?.join(', ') || 'Erro ao atualizar')
      }
    } catch (error) {
      info.revert()
      alert('Erro ao atualizar horário')
    }
  }

  handleSelect(info) {
    if (!this.canEditValue) return
    this.showCreateModal(info)
  }

  handleClick(info) {
    console.log('handleClick called', { event: info.event?.title, canEdit: this.canEditValue })
    if (!this.canEditValue) {
      info.jsEvent.preventDefault()
      return
    }
    this.showEditModal(info.event)
  }

  showCreateModal(info) {
    this.modalTitleTarget.textContent = 'Novo Horário'
    this.scheduleIdTarget.value = ''
    this.daySelectTarget.value = info.start.getDay()
    this.startTimeInputTarget.value = this.formatTime(info.start)
    this.endTimeInputTarget.value = this.formatTime(info.end)
    this.errorsTarget.textContent = ''
    this.openModal()
  }

  showEditModal(event) {
    console.log('showEditModal called', event)
    this.modalTitleTarget.textContent = 'Editar Horário'

    // Usar dados do evento diretamente em vez de fazer fetch
    this.scheduleIdTarget.value = event.id
    this.userIdSelectTarget.value = event.resourceId
    this.daySelectTarget.value = event.start.getDay()
    this.startTimeInputTarget.value = this.formatTime(event.start)
    this.endTimeInputTarget.value = this.formatTime(event.end)
    this.errorsTarget.textContent = ''
    this.openModal()
  }

  openModal() {
    const modal = new bootstrap.Modal(this.modalTarget)
    modal.show()
  }

  async handleSubmit(event) {
    console.log('handleSubmit called')
    event.preventDefault()

    const formData = new FormData(this.formTarget)
    console.log('FormData:', Object.fromEntries(formData))
    const data = {
      team_schedule: {
        user_id: parseInt(formData.get('user_id')),
        day_of_week: parseInt(formData.get('day_of_week')),
        start_time: formData.get('start_time'),
        end_time: formData.get('end_time')
      }
    }

    const id = this.scheduleIdTarget.value
    const url = id ? `/team_schedules/${id}` : '/team_schedules'
    const method = id ? 'PATCH' : 'POST'

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken()
        },
        body: JSON.stringify(data)
      })

      if (response.ok) {
        const modal = bootstrap.Modal.getInstance(this.modalTarget)
        modal.hide()
        this.refresh()
      } else {
        const result = await response.json()
        this.errorsTarget.textContent = result.errors?.join(', ') || 'Erro ao salvar'
      }
    } catch (error) {
      this.errorsTarget.textContent = 'Erro de conexão'
    }
  }

  formatTime(date) {
    return date.toTimeString().slice(0, 5)
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  refresh() {
    const params = new URLSearchParams()
    if (this.teamNameValue) params.set('team', this.teamNameValue)
    if (this.userSelectTarget?.value) params.set('user_id', this.userSelectTarget.value)
    const url = `/team_schedules${params.toString() ? '?' + params.toString() : ''}`

    fetch(url)
      .then(r => r.json())
      .then(data => {
        this.calendar.removeAllEvents()
        this.calendar.addEventSource(data.schedules)
      })
  }
}
